import { format, isValid, parse } from 'date-fns';
import type { DateRange } from '@kula-ai/components';

/**
 * Marks a picker panel so hosts with their own outside-click detection can
 * treat a click inside it as inside. kula portals the panel to
 * `document.body` with a plain `createPortal`, so unlike a Chakra portal it is
 * invisible to those checks — see `FilterContainer`.
 */
export const DATE_PANEL_CLASS = 'kula-date-picker-panel';

/** How a range is stored: ISO day strings, which is what every API wants. */
export type DateRangeValue = {
  from?: string | Date | null;
  to?: string | Date | null;
};

const ISO_DAY = 'yyyy-MM-dd';

/**
 * `new Date('2026-09-01')` is UTC midnight, which renders as August 31st
 * anywhere west of Greenwich. Every picker in the app had its own copy of
 * that bug, so day strings are parsed as local dates here instead.
 */
export const toDate = (value?: string | Date | null): Date | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) return isValid(value) ? value : undefined;
  const local = parse(value, ISO_DAY, new Date());
  if (isValid(local)) return local;
  const loose = new Date(value);
  return isValid(loose) ? loose : undefined;
};

/** The ISO day string an API expects, read off the local calendar date. */
export const toDayString = (value?: Date | null): string | null => {
  const date = toDate(value);
  return date ? format(date, ISO_DAY) : null;
};

/**
 * kula's range mode always works on an array of ranges, and wants a range
 * even when nothing is picked yet — hence the fallback to `today`.
 */
export const toRanges = (
  value: DateRangeValue | undefined,
  today: Date = new Date()
): DateRange[] => {
  const from = toDate(value?.from);
  const to = toDate(value?.to);
  return [
    {
      startDate: from ?? today,
      endDate: to ?? from ?? today,
      key: 'selection',
    },
  ];
};

/** The stored shape, back out of what kula confirmed. */
export const fromRanges = (ranges: DateRange[]): DateRangeValue => {
  const [range] = ranges ?? [];
  return {
    from: toDayString(range?.startDate),
    to: toDayString(range?.endDate),
  };
};

/**
 * The label a trigger shows. `displayFormat` is a date-fns pattern, which is
 * what `useDateFormat().dateFnsFormat` already returns.
 */
export const formatRange = (
  value: DateRangeValue | undefined,
  displayFormat: string
): string => {
  const from = toDate(value?.from);
  const to = toDate(value?.to);
  if (!from || !to) return '';
  return `${format(from, displayFormat)} - ${format(to, displayFormat)}`;
};

/**
 * The placeholder form of a date-fns pattern: `dd/MM/yyyy` reads as
 * `DD/MM/YYYY` to a user. Derived from the pattern that formats the value,
 * because `useDateFormat`'s `dateFormat.label` and `dateFnsFormat` disagree
 * when the account has no `date_fmt` set.
 */
export const toFormatHint = (displayFormat: string): string =>
  displayFormat.replace(/[dy]+/g, (token) => token.toUpperCase());

/**
 * Reads kula's in-panel "Skip weekends" checkbox. It keeps that state to
 * itself — no initial value, no change callback — so a caller that has to send
 * the flag onwards can only read the DOM. Belongs upstream in
 * `@kula-ai/components` as `skipWeekends` / `onSkipWeekendsChange`.
 */
export const readSkipWeekends = (): boolean =>
  Boolean(
    document.querySelector<HTMLInputElement>(
      `.${DATE_PANEL_CLASS} input[type="checkbox"]`
    )?.checked
  );

/**
 * Ticks kula's checkbox to match a saved value. It owns that state with no way
 * in, so the only route is a real click, which is what its own onChange reads.
 */
export const setSkipWeekends = (next: boolean): void => {
  const input = document.querySelector<HTMLInputElement>(
    `.${DATE_PANEL_CLASS} input[type="checkbox"]`
  );
  if (input && input.checked !== next) input.click();
};
